import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Loading } from '@/shared/ui';
import { useCreatePersona, useDeletePersona, useUpdatePersona } from '@/features/persona-sparks';
import {
  PERSONA_QUERY_KEYS,
  buildOptimisticProcessingPersona,
  usePersonasPolling,
  type Persona,
} from '@/entities/persona';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { getCurrentOrganizationId, usePermissions } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';
import { AudiencesV2 } from './AudiencesV2';

const AudiencesInternal = ({ workspaceId }: { workspaceId: string }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canViewAudiences } = usePermissions();

  // persona-sparks CRUD hooks owned at the page boundary and threaded into the
  // list via props. Keeps `features/audiences` boundary-clean (no
  // `@/features/persona-sparks` imports inside the slice).
  const { data: personas, isPending, isError, error, refetch } = usePersonasPolling(workspaceId);
  const createPersonaMutation = useCreatePersona();
  const updatePersonaMutation = useUpdatePersona();
  const deletePersonaMutation = useDeletePersona();

  const createPersona = useCallback(
    async (params: Parameters<typeof createPersonaMutation.mutateAsync>[0]) => {
      const result = await createPersonaMutation.mutateAsync(params);
      const listQueryKey = PERSONA_QUERY_KEYS.list(workspaceId);

      const optimisticPersona = result.personaId
        ? buildOptimisticProcessingPersona(result.personaId, params.payload, {
            workspaceId,
            organizationId: getCurrentOrganizationId(),
          })
        : null;

      if (optimisticPersona) {
        queryClient.setQueryData<Persona[]>(listQueryKey, (current) => {
          if (current?.some((persona) => persona.id === result.personaId)) {
            return current.map((persona) =>
              persona.id === result.personaId
                ? { ...persona, creationStatus: 'processing' as const }
                : persona,
            );
          }

          return [optimisticPersona, ...(current ?? [])];
        });
      }

      await queryClient.refetchQueries({ queryKey: listQueryKey });
      return result;
    },
    [createPersonaMutation, queryClient, workspaceId],
  );

  useEffect(() => {
    if (!canViewAudiences) {
      void navigate(ROUTES.KNOWLEDGE_HUB);
    }
  }, [canViewAudiences, navigate]);

  return (
    <AudiencesV2
      personas={personas}
      isPending={isPending}
      isError={isError}
      fetchError={error}
      refetch={() => void refetch()}
      onPersonaClick={(persona) => void navigate(ROUTES.KNOWLEDGE_HUB_AUDIENCE_DETAIL(persona.id))}
      onViewPersona={(id) => void navigate(ROUTES.KNOWLEDGE_HUB_AUDIENCE_DETAIL(id))}
      createPersona={createPersona}
      updatePersona={updatePersonaMutation.mutateAsync}
      deletePersona={deletePersonaMutation.mutateAsync}
      isDeleting={deletePersonaMutation.isPending}
    />
  );
};

export const Audiences = () => {
  const workspaceId = useCurrentWorkspaceId();

  if (!workspaceId) {
    return <Loading />;
  }

  return <AudiencesInternal workspaceId={workspaceId} />;
};

Audiences.displayName = 'Audiences';
